import type {
  ColumnAggregateScoreMode,
  ColumnAggregateType,
  CriterionComparisonValueMode,
  ScorecardCodeLanguage,
  ScorecardCompareOperator,
  ScorecardCompareType,
  ScorecardCountType,
  TrajectoryExpectationMode,
  TrajectoryMatchMode,
} from "../../criterionConfig";

import { AssertValidField } from "./PrimitiveEditors/AssertValidField";
import { CodeExecutionField } from "./PrimitiveEditors/CodeExecutionField";
import { ColumnAggregateFields } from "./PrimitiveEditors/ColumnAggregateFields";
import { CompareFields } from "./PrimitiveEditors/CompareFields";
import { ContainsFields } from "./PrimitiveEditors/ContainsFields";
import { CountFields } from "./PrimitiveEditors/CountFields";
import { LlmAssertionFields } from "./PrimitiveEditors/LlmAssertionFields";
import { MaxToolCallsField } from "./PrimitiveEditors/MaxToolCallsField";
import { RegexField } from "./PrimitiveEditors/RegexField";
import { TrajectoryFields } from "./PrimitiveEditors/TrajectoryFields";

import type { SmartColumn } from "@/queries/smart-tables/types";

type ScorecardAssertValidType = "object" | "number" | "sql";

interface EditCriterionFieldsRouterProps {
  isColumnAggregate: boolean;
  isContains: boolean;
  isCompare: boolean;
  isMaxToolCalls: boolean;
  isCount: boolean;
  isTrajectory: boolean;
  isLlmAssertion: boolean;
  isCodeExecution: boolean;
  isRegex: boolean;
  isAssertValid: boolean;
  columnAggregateType: ColumnAggregateType;
  onColumnAggregateTypeChange: (type: ColumnAggregateType) => void;
  columnAggregateScoreMode: ColumnAggregateScoreMode;
  onColumnAggregateScoreModeChange: (mode: ColumnAggregateScoreMode) => void;
  columnAggregateScoreMax: string;
  onColumnAggregateScoreMaxChange: (value: string) => void;
  labelColumnId: string;
  onLabelColumnIdChange: (value: string) => void;
  expectedColumnId: string;
  onExpectedColumnIdChange: (value: string) => void;
  percentile: string;
  onPercentileChange: (value: string) => void;
  sourceColumnId: string;
  onSourceColumnChange: (columnId: string) => void;
  sourceColumnOptions: { id: string; title: string }[];
  scoreContributionLabel: string;
  scoreContributionHelper: string;
  comparisonMode: CriterionComparisonValueMode;
  staticValue: string;
  matchColumnId: string;
  columns: SmartColumn[];
  onComparisonModeChange: (mode: CriterionComparisonValueMode) => void;
  onStaticValueChange: (value: string) => void;
  onMatchColumnIdChange: (columnId: string) => void;
  compareStaticPlaceholder: string;
  compareStaticDescription: string;
  hasMissingNumberStaticTarget: boolean;
  compareType: ScorecardCompareType;
  onCompareTypeChange: (type: ScorecardCompareType) => void;
  compareOperator: ScorecardCompareOperator;
  onCompareOperatorChange: (operator: ScorecardCompareOperator) => void;
  compareJsonPath: string;
  onCompareJsonPathChange: (path: string) => void;
  hasInvalidCompareConfig: boolean;
  maxCalls: string;
  onMaxCallsChange: (value: string) => void;
  assertions: string[];
  detectedPromptVariables: string[];
  onAssertionsChange: (assertions: string[]) => void;
  isLlmAssertionImportMode: boolean;
  onLlmAssertionImportModeChange: (isImportMode: boolean) => void;
  promptSourceColumnId: string;
  onPromptSourceColumnChange: (columnId: string) => void;
  variableMappings: Record<string, string>;
  onVariableMappingsChange: (mappings: Record<string, string>) => void;
  hasUnmappedLlmVariables: boolean;
  code: string;
  codeLanguage: ScorecardCodeLanguage;
  codeExecutionSourceTitles: string[];
  onCodeChange: (code: string) => void;
  onCodeLanguageChange: (language: ScorecardCodeLanguage) => void;
  regexPattern: string;
  onRegexPatternChange: (value: string) => void;
  assertValidType: ScorecardAssertValidType;
  onAssertValidTypeChange: (type: ScorecardAssertValidType) => void;
  countType: ScorecardCountType;
  onCountTypeChange: (type: ScorecardCountType) => void;
  minCount: string;
  onMinCountChange: (value: string) => void;
  maxCount: string;
  onMaxCountChange: (value: string) => void;
  hasInvalidCountBounds: boolean;
  trajectoryExpectationMode: TrajectoryExpectationMode;
  onTrajectoryExpectationModeChange: (mode: TrajectoryExpectationMode) => void;
  expectedScenarios: string[][];
  onExpectedScenariosChange: (scenarios: string[][]) => void;
  trajectoryNoToolsExpected: boolean;
  onTrajectoryNoToolsExpectedChange: (enabled: boolean) => void;
  expectedSourceColumnId: string;
  onExpectedSourceColumnChange: (columnId: string) => void;
  trajectoryMatchMode: TrajectoryMatchMode;
  onTrajectoryMatchModeChange: (mode: TrajectoryMatchMode) => void;
  hasInvalidTrajectoryExpectation: boolean;
  traceSourceColumnId: string;
}

const EditCriterionFields = ({
  isColumnAggregate,
  isContains,
  isCompare,
  isMaxToolCalls,
  isCount,
  isTrajectory,
  isLlmAssertion,
  isCodeExecution,
  isRegex,
  isAssertValid,
  columnAggregateType,
  onColumnAggregateTypeChange,
  columnAggregateScoreMode,
  onColumnAggregateScoreModeChange,
  columnAggregateScoreMax,
  onColumnAggregateScoreMaxChange,
  labelColumnId,
  onLabelColumnIdChange,
  expectedColumnId,
  onExpectedColumnIdChange,
  percentile,
  onPercentileChange,
  sourceColumnId,
  onSourceColumnChange,
  sourceColumnOptions,
  scoreContributionLabel,
  scoreContributionHelper,
  comparisonMode,
  staticValue,
  matchColumnId,
  columns,
  onComparisonModeChange,
  onStaticValueChange,
  onMatchColumnIdChange,
  compareStaticPlaceholder,
  compareStaticDescription,
  hasMissingNumberStaticTarget,
  compareType,
  onCompareTypeChange,
  compareOperator,
  onCompareOperatorChange,
  compareJsonPath,
  onCompareJsonPathChange,
  hasInvalidCompareConfig,
  maxCalls,
  onMaxCallsChange,
  assertions,
  detectedPromptVariables,
  onAssertionsChange,
  isLlmAssertionImportMode,
  onLlmAssertionImportModeChange,
  promptSourceColumnId,
  onPromptSourceColumnChange,
  variableMappings,
  onVariableMappingsChange,
  hasUnmappedLlmVariables,
  code,
  codeLanguage,
  codeExecutionSourceTitles,
  onCodeChange,
  onCodeLanguageChange,
  regexPattern,
  onRegexPatternChange,
  assertValidType,
  onAssertValidTypeChange,
  countType,
  onCountTypeChange,
  minCount,
  onMinCountChange,
  maxCount,
  onMaxCountChange,
  hasInvalidCountBounds,
  trajectoryExpectationMode,
  onTrajectoryExpectationModeChange,
  expectedScenarios,
  onExpectedScenariosChange,
  trajectoryNoToolsExpected,
  onTrajectoryNoToolsExpectedChange,
  expectedSourceColumnId,
  onExpectedSourceColumnChange,
  trajectoryMatchMode,
  onTrajectoryMatchModeChange,
  hasInvalidTrajectoryExpectation,
  traceSourceColumnId,
}: EditCriterionFieldsRouterProps) => (
  <>
    {isColumnAggregate ? (
      <ColumnAggregateFields
        columnAggregateType={columnAggregateType}
        onColumnAggregateTypeChange={onColumnAggregateTypeChange}
        columnAggregateScoreMode={columnAggregateScoreMode}
        onColumnAggregateScoreModeChange={onColumnAggregateScoreModeChange}
        columnAggregateScoreMax={columnAggregateScoreMax}
        onColumnAggregateScoreMaxChange={onColumnAggregateScoreMaxChange}
        labelColumnId={labelColumnId}
        onLabelColumnIdChange={onLabelColumnIdChange}
        expectedColumnId={expectedColumnId}
        onExpectedColumnIdChange={onExpectedColumnIdChange}
        percentile={percentile}
        onPercentileChange={onPercentileChange}
        sourceColumnId={sourceColumnId}
        sourceColumnOptions={sourceColumnOptions}
        scoreContributionLabel={scoreContributionLabel}
        scoreContributionHelper={scoreContributionHelper}
      />
    ) : null}

    {isContains ? (
      <ContainsFields
        mode={comparisonMode}
        staticValue={staticValue}
        columnId={matchColumnId}
        columns={columns}
        onModeChange={onComparisonModeChange}
        onStaticValueChange={onStaticValueChange}
        onColumnChange={onMatchColumnIdChange}
      />
    ) : null}

    {isCompare ? (
      <CompareFields
        mode={comparisonMode}
        staticValue={staticValue}
        columnId={matchColumnId}
        columns={columns}
        onModeChange={onComparisonModeChange}
        onStaticValueChange={onStaticValueChange}
        onColumnChange={onMatchColumnIdChange}
        compareStaticPlaceholder={compareStaticPlaceholder}
        compareStaticDescription={compareStaticDescription}
        hasMissingNumberStaticTarget={hasMissingNumberStaticTarget}
        compareType={compareType}
        onCompareTypeChange={onCompareTypeChange}
        compareOperator={compareOperator}
        onCompareOperatorChange={onCompareOperatorChange}
        compareJsonPath={compareJsonPath}
        onCompareJsonPathChange={onCompareJsonPathChange}
        hasInvalidCompareConfig={hasInvalidCompareConfig}
      />
    ) : null}

    {isMaxToolCalls ? (
      <MaxToolCallsField maxCalls={maxCalls} onMaxCallsChange={onMaxCallsChange} />
    ) : null}

    {isCount ? (
      <CountFields
        countType={countType}
        onCountTypeChange={onCountTypeChange}
        minCount={minCount}
        onMinCountChange={onMinCountChange}
        maxCount={maxCount}
        onMaxCountChange={onMaxCountChange}
        hasInvalidBounds={hasInvalidCountBounds}
      />
    ) : null}

    {isTrajectory ? (
      <TrajectoryFields
        expectationMode={trajectoryExpectationMode}
        onExpectationModeChange={onTrajectoryExpectationModeChange}
        expectedScenarios={expectedScenarios}
        onExpectedScenariosChange={onExpectedScenariosChange}
        noToolsExpected={trajectoryNoToolsExpected}
        onNoToolsExpectedChange={onTrajectoryNoToolsExpectedChange}
        expectedSourceColumnId={expectedSourceColumnId}
        onExpectedSourceColumnChange={onExpectedSourceColumnChange}
        sourceColumnOptions={sourceColumnOptions}
        matchMode={trajectoryMatchMode}
        onMatchModeChange={onTrajectoryMatchModeChange}
        hasInvalidExpectation={hasInvalidTrajectoryExpectation}
        traceSourceColumnId={traceSourceColumnId}
      />
    ) : null}

    {isLlmAssertion ? (
      <LlmAssertionFields
        sourceColumnId={sourceColumnId}
        onSourceColumnChange={onSourceColumnChange}
        assertions={assertions}
        detectedPromptVariables={detectedPromptVariables}
        onAssertionsChange={onAssertionsChange}
        isImportMode={isLlmAssertionImportMode}
        onImportModeChange={onLlmAssertionImportModeChange}
        promptSourceColumnId={promptSourceColumnId}
        onPromptSourceColumnChange={onPromptSourceColumnChange}
        variableMappings={variableMappings}
        onVariableMappingsChange={onVariableMappingsChange}
        sourceColumnOptions={sourceColumnOptions}
        hasUnmappedLlmVariables={hasUnmappedLlmVariables}
      />
    ) : null}

    {isCodeExecution ? (
      <CodeExecutionField
        code={code}
        codeLanguage={codeLanguage}
        sourceTitles={codeExecutionSourceTitles}
        onCodeChange={onCodeChange}
        onLanguageChange={onCodeLanguageChange}
      />
    ) : null}

    {isRegex ? (
      <RegexField regexPattern={regexPattern} onRegexPatternChange={onRegexPatternChange} />
    ) : null}

    {isAssertValid ? (
      <AssertValidField
        assertValidType={assertValidType}
        onAssertValidTypeChange={onAssertValidTypeChange}
      />
    ) : null}
  </>
);

export default EditCriterionFields;
